import { app, menu } from '../utils/store'

class Menu {
    constructor() {
        this.onAfterClose = null
        this.onBeforeToggle = null
    }

    getTargetAttribute(submenu = false) {
        return submenu ? 'data-submenu-target' : 'data-menu-target'
    }

    getTargetId(entry, submenu = false) {
        return entry?.getAttribute(this.getTargetAttribute(submenu)) || null
    }

    setTogglerState(entry, isOpen) {
        if (!entry) {
            return
        }

        entry.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
        entry.classList.toggle('active', isOpen)
    }

    getToggler(menuId, submenu = false) {
        const attribute = this.getTargetAttribute(submenu)

        return document.querySelector(`[${attribute}="${menuId}"]`)
    }

    getMegaPanel(entry) {
        return entry?.closest('[data-menu-mega]') || null
    }

    open(target, menuId) {
        if (!target || !menuId) {
            return
        }

        target.classList.add('active')
        app.body.classList.add('menu--open')
        app.body.classList.add(`menu--${menuId}`)
        app.nav?.setAttribute('mode', 'light')

        menu.id = menuId
        menu.isOpen = true
        menu.current = menuId

        this.setTogglerState(this.getToggler(menuId), true)
    }

    setSubmenuListVisibility(container, listId) {
        if (!container) {
            return
        }

        container.querySelectorAll('ul.submenu').forEach((list) => {
            list.classList.toggle('active', Boolean(listId && list.id === listId))
        })
    }

    openSubmenu(target, menuId, entry) {
        if (!target || !menuId || !entry) {
            return
        }

        const listId = entry.getAttribute('data-submenu-list')
        const mega = this.getMegaPanel(entry)

        if (mega) {
            mega.classList.add('menu--submenu-open')
        }

        target.classList.add('active')
        app.body.classList.add('menu--submenu-open')
        app.body.classList.add(`menu--submenu-${menuId}`)

        menu.submenu.isOpen = true
        menu.submenu.current = menuId
        menu.submenu.listId = listId
        menu.submenu.toggler = entry

        this.setSubmenuListVisibility(target, listId)

        document.querySelectorAll(`[data-submenu-target="${menuId}"]`).forEach((toggler) => {
            this.setTogglerState(toggler, toggler === entry)
        })
    }

    closeSubmenu() {
        if (!menu.submenu.isOpen) {
            return
        }

        const target = menu.submenu.current
            ? document.getElementById(menu.submenu.current)
            : null

        if (target) {
            target.classList.remove('active')
            this.setSubmenuListVisibility(target, null)
        }

        document.querySelectorAll('[data-menu-mega].menu--submenu-open').forEach((mega) => {
            mega.classList.remove('menu--submenu-open')
        })

        app.body.classList.remove('menu--submenu-open')

        Array.from(app.body.classList).forEach((className) => {
            if (className.startsWith('menu--submenu-') && className !== 'menu--submenu-open') {
                app.body.classList.remove(className)
            }
        })

        document.querySelectorAll('[data-submenu-target]').forEach((entry) => {
            this.setTogglerState(entry, false)
        })

        menu.submenu.isOpen = false
        menu.submenu.current = undefined
        menu.submenu.listId = undefined
        menu.submenu.toggler = undefined
    }

    closeAll(submenuOnly = false) {
        if (submenuOnly) {
            this.closeSubmenu()
            return
        }

        this.closeSubmenu()

        menu.id = undefined
        menu.isOpen = false
        menu.current = undefined

        document.querySelectorAll('[data-menu-mega]').forEach((entry) => {
            entry.classList.remove('active')
            entry.classList.remove('menu--submenu-open')
        })

        app.body.classList.remove('menu--open')

        Array.from(app.body.classList).forEach((className) => {
            if (className.startsWith('menu--') && className !== 'menu--open' && !className.startsWith('menu--submenu')) {
                app.body.classList.remove(className)
            }
        })

        document.querySelectorAll('[data-menu-target]').forEach((entry) => {
            this.setTogglerState(entry, false)
        })

        if (typeof this.onAfterClose === 'function') {
            this.onAfterClose()
        }
    }

    toggle(entry, submenu = false) {
        entry.onclick = (event) => {
            event.stopPropagation()
            event.preventDefault()

            if (typeof this.onBeforeToggle === 'function') {
                this.onBeforeToggle(entry, submenu)
            }

            const menuId = this.getTargetId(entry, submenu)
            const target = menuId ? document.getElementById(menuId) : null

            if (!target) {
                return
            }

            if (submenu) {
                const isSameSubmenu = menu.submenu.isOpen
                    && menu.submenu.current === menuId
                    && menu.submenu.toggler === entry

                if (isSameSubmenu) {
                    this.closeSubmenu()
                    return
                }

                this.closeSubmenu()
                this.openSubmenu(target, menuId, entry)
                return
            }

            if (menu.isOpen && menu.current === menuId) {
                this.closeAll()
                return
            }

            this.closeAll()
            this.open(target, menuId)
        }
    }

    close(entry, submenu = false) {
        entry.onclick = (event) => {
            event.stopPropagation()
            event.preventDefault()

            if (submenu || menu.submenu.isOpen) {
                this.closeSubmenu()
                return
            }

            this.closeAll()
        }
    }
}

export const MenuManager = new Menu()
